import type { Component } from 'vue'

/**
 * The Vue islands the React shell can mount, by name. One line per island;
 * retiring an island is deleting its line (spec §5.2, "one island registration
 * is deleted"). Loaders, not components: each island stays its own chunk.
 */
export const islands = {
  map: () => import('@/islands/MapIsland.vue'),
} satisfies Record<string, () => Promise<{ default: Component }>>

export type IslandName = keyof typeof islands

const loading = new Map<IslandName, Promise<Component>>()

/**
 * The island's component, loaded once; the same promise every time, so `use()`
 * can suspend on it.
 *
 * A REJECTED promise is dropped from the cache. `use()` rethrows a cached
 * rejection on every subsequent render, so keeping it would make one failed
 * chunk request — a redeploy is enough to 404 a hashed chunk for an open tab —
 * permanent for the life of the page, with no re-render able to recover.
 */
export function loadIsland(name: IslandName): Promise<Component> {
  let promise = loading.get(name)
  if (!promise) {
    promise = islands[name]().then((m) => m.default)
    promise.catch(() => {
      if (loading.get(name) === promise) loading.delete(name)
    })
    loading.set(name, promise)
  }
  return promise
}
